import { useForm, Resolver } from 'react-hook-form';
import {
  Text,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  useToast,
  InputGroup,
  Box,
} from '@chakra-ui/react';
import { modalFormSchema, type ModalFormData } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { Predictions, PredictionsStatus } from '@/model/predictions';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePutPredictionsMutation } from '@/services/queries/predictions';
import { useEffect, useRef, useMemo } from 'react';
import { CircleFileUpload } from '@/components/CircleFileUpload';
import { ImagePreview } from '@/components/ImagePreview';
import { v4 as uuidv4 } from 'uuid';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { compressImage } from '@/utils/image';

export interface PredictionsEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Predictions;
  onClose: () => void;
  onDone?: () => void;
}

export const PredictionsEditModal = ({ isOpen, onClose, onDone, item }: PredictionsEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putMutation = usePutPredictionsMutation();

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const resolver: Resolver<ModalFormData> = zodResolver(modalFormSchema);

  const form = useForm<ModalFormData>({
    resolver,
    defaultValues: {
      team_uuid: '',
      identifier: '',
      title: '',
      description: '',
      status: PredictionsStatus.NOT_PUBLISHED,
      disable_comments: false,
      enable_home: true,
      has_closing_date: false,
      closing_date: undefined,
    },
  });

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
    handleSubmit,
    control,
  } = form;
  const formValues = watch();

  const isLocked =
    (item.option_a_result !== null && item.option_a_result !== undefined && item.option_a_result !== '') ||
    (item.option_b_result !== null && item.option_b_result !== undefined && item.option_b_result !== '');

  const renameFile = (file: File, newName: string): File => {
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const handlePreviewActionClick = (index: number) => {
    const indexToLetter = {
      0: 'a',
      1: 'b',
    } as const;

    const letter = indexToLetter[index as 0 | 1];

    setValue(`option_${letter}_image`, undefined);

    setTimeout(() => {
      inputRefs.current[index]?.click();
    }, 100);
  };

  const handlePreviewDeleteClick = (index: number) => {
    const indexToLetter = {
      0: 'a',
      1: 'b',
    } as const;

    const letter = indexToLetter[index as 0 | 1];

    if (inputRefs?.current?.[index]) {
      const input = inputRefs.current[index] as HTMLInputElement;
      input.value = '';
      input.files = null;
    }

    setValue(`option_${letter}_image`, undefined);

    setTimeout(() => {
      inputRefs.current[index]?.click();
    }, 100);
  };

  const handleEdit = (data: ModalFormData) => {
    const predictionsOptions: { uuid?: string; option_name: string }[] = [];
    const mediaFiles: Record<string, File> = {};

    // Opção A
    if (data.option_a_image instanceof File) {
      const uuidOptionA = uuidv4();
      const renamed = renameFile(data.option_a_image, `${uuidOptionA}.${data.option_a_image.name.split('.').pop()}`);
      predictionsOptions.push({ uuid: uuidOptionA, option_name: data.option_a_name });
      mediaFiles[uuidOptionA] = renamed;
    } else {
      predictionsOptions.push({ option_name: data.option_a_name });
    }

    // Opção B
    if (data.option_b_image instanceof File) {
      const uuidOptionB = uuidv4();
      const renamed = renameFile(data.option_b_image, `${uuidOptionB}.${data.option_b_image.name.split('.').pop()}`);
      predictionsOptions.push({ uuid: uuidOptionB, option_name: data.option_b_name });
      mediaFiles[uuidOptionB] = renamed;
    } else {
      predictionsOptions.push({ option_name: data.option_b_name });
    }

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      identifier: data.identifier,
      title: data.title,
      description: data.description,
      closing_date: data.has_closing_date ? data.closing_date : undefined,
      disable_comments: data.disable_comments,
      enable_home: data.enable_home,
      status: data.status as PredictionsStatus,
      predictions_options: predictionsOptions,
      ...mediaFiles,
    };

    putMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_success'),
            status: 'success',
          });

          onDone?.();
          reset();
        },
        onError: (e: any) => {
          toast({
            title: e?.message || t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_error'),
            status: 'error',
          });
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  useEffect(() => {
    if (!item) return;

    setValue('team_uuid', item.team_uuid);
    setValue('identifier', item.identifier);
    setValue('title', item.title);
    setValue('description', item.description);
    setValue('status', item.status);
    setValue('option_a_name', item.option_a_name);
    setValue('option_a_image', item.option_a_image_url);
    setValue('option_b_name', item.option_b_name);
    setValue('option_b_image', item.option_b_image_url);
    setValue('disable_comments', item.disable_comments);
    setValue('enable_home', item.enable_home);
    setValue('has_closing_date', !!item.closing_date);
    setValue(
      'closing_date',
      item.closing_date ? formatDateToDateTimeLocalInputValue(item.closing_date) : '',
    );
  }, [setValue, item]);

  const imagePreviewUrls = useMemo(() => {
    return {
      optionA: formValues?.option_a_image
        ? formValues.option_a_image instanceof File
          ? URL.createObjectURL(formValues.option_a_image)
          : formValues.option_a_image
        : undefined,
      optionB: formValues?.option_b_image
        ? formValues.option_b_image instanceof File
          ? URL.createObjectURL(formValues.option_b_image)
          : formValues.option_b_image
        : undefined,
    };
  }, [formValues?.option_a_image, formValues?.option_b_image]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrls.optionA && imagePreviewUrls.optionA.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrls.optionA);
      }
      if (imagePreviewUrls.optionB && imagePreviewUrls.optionB.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrls.optionB);
      }
    };
  }, [imagePreviewUrls]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(handleEdit)}>
          <ModalHeader display='flex' justifyContent='space-between' alignItems='center' gap={2}>
            {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_title')}
            <Flex gap={2} alignItems='center'>
              <Text fontSize='14px' fontWeight='normal'>
                {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_published_label')}
              </Text>
              <Switch
                isChecked={formValues.status === PredictionsStatus.PUBLISHED}
                onChange={event =>
                  form.setValue('status', event.target.checked ? PredictionsStatus.PUBLISHED : PredictionsStatus.NOT_PUBLISHED)
                }
                display='flex'
                alignItems='center'
                size='sm'
              />
            </Flex>
          </ModalHeader>
          <ModalBody>
            <Flex direction='column' gap={4}>
              <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
                control={control}
                name='team_uuid'
                prefetchOnOpen
                label={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_team_label')}
                placeholder={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_team_label')}
                showFlag
                width='100%'
                usePortal={false}
                error={errors?.team_uuid?.message as string | undefined}
              />

              <InputField
                isDisabled={isLocked || item.status !== PredictionsStatus.DRAFT}
                withoutFloating
                label={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_identifier_label')}
                helperText={t(
                  'pages.application_predictions.components.predictions_edit_modal.predictions_edit_identifier_helper_text',
                )}
                maxLength={20}
                {...register('identifier')}
                rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
              />

              <InputField
                isDisabled={isLocked || item.status !== PredictionsStatus.DRAFT}
                withoutFloating
                label={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_title_label')}
                helperText={t(
                  'pages.application_predictions.components.predictions_edit_modal.predictions_edit_title_helper_text',
                )}
                maxLength={50}
                {...register('title')}
                rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
              />

              <InputField
                isDisabled={isLocked || item.status !== PredictionsStatus.DRAFT}
                withoutFloating
                label={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_description_label')}
                helperText={t(
                  'pages.application_predictions.components.predictions_edit_modal.predictions_edit_description_helper_text',
                )}
                maxLength={100}
                {...register('description')}
                rootProps={{ isInvalid: !!errors?.description, errorMessage: errors?.description?.message }}
              />

              <InputGroup display='flex' flexDirection='column' gap={3}>
                <Text fontWeight='medium'>
                  {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_prediction_label')}
                </Text>

                <Box>
                  <Flex gap={3} alignItems='center'>
                    {!formValues.option_a_image ? (
                      <Flex minW='80px' w='80px' minH='80px' h='80px' justifyContent='center' alignItems='center'>
                        <CircleFileUpload
                          disabled={isLocked || item.status !== PredictionsStatus.DRAFT}
                          ref={el => (inputRefs.current[0] = el)}
                          label={t(
                            'pages.application_predictions.components.predictions_edit_modal.predictions_edit_image_label',
                          )}
                          onFileChange={async file => {
                            if (!file) return;
                            const compressedFile = await compressImage(file);
                            form.setValue('option_a_image', compressedFile);
                          }}
                        />
                      </Flex>
                    ) : (
                      <ImagePreview
                        src={imagePreviewUrls.optionA}
                        actionIconName='image'
                        onActionClick={item.status === PredictionsStatus.DRAFT ? () => handlePreviewActionClick(0) : undefined}
                        onDeleteClick={item.status === PredictionsStatus.DRAFT ? () => handlePreviewDeleteClick(0) : undefined}
                      />
                    )}

                    <InputField
                      isDisabled={isLocked || item.status !== PredictionsStatus.DRAFT}
                      withoutFloating
                      label={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_option_1_label')}
                      helperText={t(
                        'pages.application_predictions.components.predictions_edit_modal.predictions_edit_option_1_helper_text',
                      )}
                      maxLength={50}
                      width='100%'
                      {...form.register('option_a_name')}
                      rootProps={{
                        isInvalid: !!errors?.option_a_name,
                        errorMessage: errors?.option_a_name?.message?.toString(),
                      }}
                    />
                  </Flex>

                  {form.formState.errors?.option_a_image ? (
                    <Text fontSize='14px' color='red.500'>
                      {form.formState.errors?.option_a_image?.message?.toString()}
                    </Text>
                  ) : null}
                </Box>

                <Box>
                  <Flex gap={3} alignItems='center'>
                    {!formValues.option_b_image ? (
                      <Flex minW='80px' w='80px' minH='80px' h='80px' justifyContent='center' alignItems='center'>
                        <CircleFileUpload
                          disabled={isLocked || item.status !== PredictionsStatus.DRAFT}
                          ref={el => (inputRefs.current[1] = el)}
                          label={t(
                            'pages.application_predictions.components.predictions_edit_modal.predictions_edit_image_label',
                          )}
                          onFileChange={async file => {
                            if (!file) return;
                            const compressedFile = await compressImage(file);
                            form.setValue('option_b_image', compressedFile);
                          }}
                        />
                      </Flex>
                    ) : (
                      <ImagePreview
                        src={imagePreviewUrls.optionB}
                        actionIconName='image'
                        onActionClick={item.status === PredictionsStatus.DRAFT ? () => handlePreviewActionClick(1) : undefined}
                        onDeleteClick={item.status === PredictionsStatus.DRAFT ? () => handlePreviewDeleteClick(1) : undefined}
                      />
                    )}

                    <InputField
                      isDisabled={isLocked || item.status !== PredictionsStatus.DRAFT}
                      withoutFloating
                      label={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_option_2_label')}
                      helperText={t(
                        'pages.application_predictions.components.predictions_edit_modal.predictions_edit_option_2_helper_text',
                      )}
                      maxLength={50}
                      width='100%'
                      {...form.register('option_b_name')}
                      rootProps={{
                        isInvalid: !!errors?.option_b_name,
                        errorMessage: errors?.option_b_name?.message?.toString(),
                      }}
                    />
                  </Flex>

                  {form.formState.errors?.option_b_image ? (
                    <Text fontSize='14px' color='red.500'>
                      {form.formState.errors?.option_b_image?.message?.toString()}
                    </Text>
                  ) : null}
                </Box>
              </InputGroup>

              <Switch
                isDisabled={isLocked}
                isChecked={!!formValues.has_closing_date}
                onChange={event => setValue('has_closing_date', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_set_closing_date')}
              </Switch>

              {formValues?.has_closing_date ? (
                <InputField
                  withoutFloating
                  label={t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_closing_date_label')}
                  type='datetime-local'
                  fullDatePickerArea
                  min={formatDateToDateTimeLocalInputValue(
                    item.closing_date && new Date(item.closing_date) < new Date() ? item.closing_date : new Date(),
                  )}
                  rootProps={{ isInvalid: !!errors?.closing_date, errorMessage: errors?.closing_date?.message }}
                  {...register('closing_date')}
                />
              ) : null}

              <Switch
                isChecked={!!formValues.disable_comments}
                onChange={event => setValue('disable_comments', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_disable_comments')}
              </Switch>

              <Switch
                isChecked={!!formValues.enable_home}
                onChange={event => setValue('enable_home', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_enable_home')}
              </Switch>              
            </Flex>
          </ModalBody>

          <ModalFooter gap='kilo'>
            <Button type='button' variant='outlined' color='gray.600' onClick={handleClose}>
              {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_cancel')}
            </Button>
            <Button type='submit'>
                {t('pages.application_predictions.components.predictions_edit_modal.predictions_edit_save_changes')}
              </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={putMutation.isLoading} />
    </>
  );
};
